/**
 * 🏦 Bank Account Verification Dashboard
 * Admin page for managing bank account NIN verification
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Pagination,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Stack,
  CardContent,
  TableContainer,
  InputLabel,
  FormControl,
  Paper,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  AccountBalance,
  Verified,
  Cancel,
  Warning,
  Refresh,
  Download,
  Visibility,
  CheckCircle,
  Block,
  Edit,
  AccountCircle,
  ReportProblem,
} from '@mui/icons-material';
import {
  bankVerificationService,
  type BankAccount,
  type BankVerificationSummary,
} from '@/services/bankVerificationService';
import toast from 'react-hot-toast';
import { generateUserReference } from '@/utils/userReferenceGenerator';

export default function BankVerificationPage() {
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [summary, setSummary] = useState<BankVerificationSummary>({
    totalAccounts: 0,
    approvedCount: 0,
    pendingCount: 0,
    rejectedCount: 0,
    ninVerifiedCount: 0,
    manualApprovalCount: 0,
    passportUsers: 0,
    accountsWithMismatches: 0,
  });

  // Override dialog
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null);
  const [overrideAction, setOverrideAction] = useState<'approve' | 'reject'>('approve');
  const [overrideReason, setOverrideReason] = useState<string>('');
  const [overrideJustification, setOverrideJustification] = useState('');
  const [overrideLoading, setOverrideLoading] = useState(false);

  // Details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    loadBankAccounts();
  }, [page, limit, statusFilter, searchQuery]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadBankAccounts();
    }, 30000);

    return () => clearInterval(interval);
  }, [page, limit, statusFilter, searchQuery]);

  const loadBankAccounts = async () => {
    setLoading(true);
    try {
      const response = await bankVerificationService.getBankAccounts({
        page,
        limit,
        status: statusFilter === 'all' ? undefined : statusFilter as any,
        search: searchQuery,
      });

      if (response.success && response.data) {
        setBankAccounts(response.data.bankAccounts); // Service already maps "accounts" to "bankAccounts"

        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages);
          setTotalCount(response.data.pagination.totalCount);
        } else {
          setTotalPages(1);
          setTotalCount(response.data.bankAccounts.length);
        }

        setSummary(response.data.summary);
      } else {
        setBankAccounts([]);
        setTotalPages(1);
        setTotalCount(0);
        console.error('API error:', response.error);

        if (!response.error?.includes('500') && !response.error?.includes('Internal server error')) {
          toast.error(response.error || 'Failed to load bank accounts');
        }
      }
    } catch (error) {
      console.error('Failed to load bank accounts:', error);
      setBankAccounts([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleOverride = async () => {
    if (!selectedAccount || !overrideReason || !overrideJustification.trim()) {
      toast.error('Please select a reason and provide justification');
      return;
    }

    setOverrideLoading(true);
    try {
      const response = await bankVerificationService.overrideVerification(
        selectedAccount.id,
        {
          action: overrideAction,
          reason: overrideReason as any,
          justification: overrideJustification,
        }
      );

      if (response.success) {
        toast.success(`Bank account ${overrideAction === 'approve' ? 'approved' : 'rejected'} successfully!`);
        setOverrideDialogOpen(false);
        setOverrideAction('approve');
        setOverrideReason('');
        setOverrideJustification('');
        setSelectedAccount(null);
        await loadBankAccounts();
      } else {
        console.error('Override verification failed:', response.error);
        toast.error(response.error || 'Failed to override verification');
      }
    } catch (error: any) {
      console.error('Failed to override verification:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to override verification';
      toast.error(errorMessage);
    } finally {
      setOverrideLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const blob = await bankVerificationService.exportToCSV({
        status: statusFilter === 'all' ? undefined : statusFilter as any,
        search: searchQuery,
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bank-verification-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      toast.success('Bank verification data exported successfully!');
    } catch (error) {
      console.error('Failed to export CSV:', error);
      toast.error('Failed to export data');
    }
  };

  const getStatusColor = (
    status: string
  ): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending_review':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'pending_review':
        return 'Pending Review';
      default:
        return status;
    }
  };

  const StatCard = ({ title, value, icon, color }: any) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {value.toLocaleString()}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
            Bank Account Verification
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage NIN verification status for all bank accounts
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportCSV}
            disabled={!bankAccounts || bankAccounts.length === 0}
          >
            Export CSV
          </Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadBankAccounts}>
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Accounts"
            value={summary.totalAccounts}
            icon={<AccountBalance sx={{ color: 'white' }} />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Approved"
            value={summary.approvedCount}
            icon={<CheckCircle sx={{ color: 'white' }} />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Review"
            value={summary.pendingCount}
            icon={<Warning sx={{ color: 'white' }} />}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Rejected"
            value={summary.rejectedCount}
            icon={<Cancel sx={{ color: 'white' }} />}
            color="error.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="NIN Verified"
            value={summary.ninVerifiedCount}
            icon={<Verified sx={{ color: 'white' }} />}
            color="success.light"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Manual Approval"
            value={summary.manualApprovalCount}
            icon={<Edit sx={{ color: 'white' }} />}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Passport Users"
            value={summary.passportUsers}
            icon={<AccountCircle sx={{ color: 'white' }} />}
            color="secondary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="With Mismatches"
            value={summary.accountsWithMismatches}
            icon={<ReportProblem sx={{ color: 'white' }} />}
            color="error.light"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Search by user name, email, account number..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="pending_review">Pending Review</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Per Page</InputLabel>
              <Select
                value={limit}
                label="Per Page"
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
              >
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="text.secondary" align="right" sx={{ lineHeight: '40px' }}>
              {totalCount > 0 ? (
                <>
                  {(page - 1) * limit + 1}-{Math.min(page * limit, totalCount)} of {totalCount}
                </>
              ) : (
                '0 accounts'
              )}
            </Typography>
          </Grid>
        </Grid>
      </Card>

      {/* Bank Accounts Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Bank Account</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Verification Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date Added</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : bankAccounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <AccountBalance sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      No bank accounts found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {statusFilter === 'all'
                        ? 'No bank accounts have been added yet'
                        : `No ${statusFilter} bank accounts at the moment`}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                bankAccounts.map((account) => (
                  <TableRow key={account.id} hover>
                    {/* User */}
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {account.user.fullName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {account.user.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {account.user.country}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Bank Account */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {account.bankName}
                        </Typography>
                        {account.isPrimary && (
                          <Chip
                            label="Primary"
                            size="small"
                            color="primary"
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {account.accountNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {account.accountName}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Chip
                        label={getStatusLabel(account.approvalStatus)}
                        color={getStatusColor(account.approvalStatus)}
                        size="small"
                        sx={{ fontWeight: 600 }}
                        icon={
                          account.adminOverride ? (
                            <Edit sx={{ fontSize: 16 }} />
                          ) : account.isNinVerified ? (
                            <Verified sx={{ fontSize: 16 }} />
                          ) : undefined
                        }
                      />
                      {account.adminOverride && (
                        <Typography variant="caption" color="info.main" display="block" sx={{ mt: 0.5 }}>
                          Admin Override
                        </Typography>
                      )}
                      {account.approvalStatus === 'pending_review' && (
                        <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5, fontWeight: 600 }}>
                          ⚠️ Cannot receive payouts
                        </Typography>
                      )}
                      {account.mismatchAttempts > 0 && (
                        <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                          {account.mismatchAttempts} mismatch{account.mismatchAttempts > 1 ? 'es' : ''}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Verification Type */}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                          label={account.user.verificationDocumentType?.toUpperCase() || 'None'}
                          size="small"
                          variant="outlined"
                          color={account.user.verificationDocumentType === 'nin' ? 'primary' : 'secondary'}
                        />
                        {account.requiresManualApproval && (
                          <Chip
                            label="Manual Approval"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>

                    {/* Date Added */}
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(account.createdAt).toLocaleDateString('en-NG', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </Typography>
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedAccount(account);
                              setDetailsDialogOpen(true);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>

                        {/* Show review button for pending, rejected, or accounts requiring manual approval */}
                        {(account.approvalStatus === 'pending_review' ||
                          account.approvalStatus === 'rejected' ||
                          account.requiresManualApproval ||
                          !account.adminOverride) && (
                          <Tooltip title={account.approvalStatus === 'approved' ? 'Re-review Account' : 'Review Account'}>
                            <IconButton
                              size="small"
                              color={account.approvalStatus === 'pending_review' ? 'warning' : 'primary'}
                              onClick={() => {
                                setSelectedAccount(account);
                                setOverrideDialogOpen(true);
                              }}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Page {page} of {totalPages} ({totalCount} total accounts)
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
            />
            <Box sx={{ width: 200 }} />
          </Box>
        )}
      </Card>

      {/* Override Dialog */}
      <Dialog
        open={overrideDialogOpen}
        onClose={() => setOverrideDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit color="warning" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Review Bank Account Verification
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> This will manually override the verification status for this bank account.
              Only use this for legitimate cases with proper justification.
            </Typography>
          </Alert>

          {selectedAccount && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1, border: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Account Information:
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>User:</strong> {selectedAccount.user.fullName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Bank:</strong> {selectedAccount.bankName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Account:</strong> {selectedAccount.accountNumber} - {selectedAccount.accountName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>NIN Legal Name:</strong> {selectedAccount.user.ninLegalName || 'N/A'}
              </Typography>
              <Typography variant="body2" color={selectedAccount.mismatchAttempts > 0 ? 'warning.main' : 'text.primary'}>
                <strong>Mismatch Attempts:</strong> {selectedAccount.mismatchAttempts}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Action</InputLabel>
              <Select
                value={overrideAction}
                label="Action"
                onChange={(e) => setOverrideAction(e.target.value as 'approve' | 'reject')}
              >
                <MenuItem value="approve">Approve</MenuItem>
                <MenuItem value="reject">Reject</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Reason</InputLabel>
              <Select
                value={overrideReason}
                label="Reason"
                onChange={(e) => setOverrideReason(e.target.value)}
              >
                <MenuItem value="legal_name_change">Legal Name Change</MenuItem>
                <MenuItem value="passport_verification">Passport Verification</MenuItem>
                <MenuItem value="name_variation">Name Variation (Cultural/Regional)</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Justification"
              multiline
              rows={4}
              value={overrideJustification}
              onChange={(e) => setOverrideJustification(e.target.value)}
              placeholder="Provide a detailed justification for this decision..."
              required
              helperText="This will be logged in the audit trail and visible to compliance team"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOverrideDialogOpen(false)} disabled={overrideLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleOverride}
            variant="contained"
            color={overrideAction === 'approve' ? 'success' : 'error'}
            disabled={overrideLoading || !overrideReason || !overrideJustification.trim()}
            startIcon={overrideLoading ? <CircularProgress size={20} /> : overrideAction === 'approve' ? <CheckCircle /> : <Block />}
          >
            {overrideLoading ? 'Processing...' : overrideAction === 'approve' ? 'Approve Account' : 'Reject Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Bank Account Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedAccount && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              {/* User Info */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  User Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Full Name
                    </Typography>
                    <Typography variant="body2">{selectedAccount.user.fullName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body2">{selectedAccount.user.email}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body2">{selectedAccount.user.phone}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Country
                    </Typography>
                    <Typography variant="body2">{selectedAccount.user.country}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Verification Document
                    </Typography>
                    <Chip
                      label={selectedAccount.user.verificationDocumentType?.toUpperCase() || 'None'}
                      color={selectedAccount.user.verificationDocumentType === 'nin' ? 'primary' : 'secondary'}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      NIN Legal Name
                    </Typography>
                    <Typography variant="body2">{selectedAccount.user.ninLegalName || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Bank Account Info */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Bank Account Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Bank Name
                    </Typography>
                    <Typography variant="body2">{selectedAccount.bankName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Bank Code
                    </Typography>
                    <Typography variant="body2">{selectedAccount.bankCode}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Account Number
                    </Typography>
                    <Typography variant="body2">{selectedAccount.accountNumber}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Account Name
                    </Typography>
                    <Typography variant="body2">{selectedAccount.accountName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Bank Country
                    </Typography>
                    <Typography variant="body2">{selectedAccount.bankCountry}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Verification Info */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Verification Status
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Approval Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={getStatusLabel(selectedAccount.approvalStatus)}
                        color={getStatusColor(selectedAccount.approvalStatus)}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      NIN Verified
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedAccount.isNinVerified ? 'Yes' : 'No'}
                        color={selectedAccount.isNinVerified ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Requires Manual Approval
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedAccount.requiresManualApproval ? 'Yes' : 'No'}
                        color={selectedAccount.requiresManualApproval ? 'warning' : 'default'}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Primary Account
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedAccount.isPrimary ? 'Yes' : 'No'}
                        color={selectedAccount.isPrimary ? 'primary' : 'default'}
                        size="small"
                        icon={selectedAccount.isPrimary ? <CheckCircle sx={{ fontSize: 16 }} /> : undefined}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Mismatch Attempts
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: selectedAccount.mismatchAttempts > 0 ? 'warning.main' : 'text.primary',
                      }}
                    >
                      {selectedAccount.mismatchAttempts}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Override Info */}
              {selectedAccount.adminOverride && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Admin Override Information
                    </Typography>
                    <Alert severity="info">
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Action:</strong> {selectedAccount.adminOverride.action.toUpperCase()}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Overridden By:</strong> {selectedAccount.adminOverride.overriddenByEmail}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Date:</strong>{' '}
                        {new Date(selectedAccount.adminOverride.overriddenAt).toLocaleString('en-NG')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Reason:</strong> {selectedAccount.adminOverride.reason}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Justification:</strong> {selectedAccount.adminOverride.justification}
                      </Typography>
                    </Alert>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDetailsDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
